import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {
    competitionRegistrationStatusColor,
    competitionRegistrationStatusLabel,
    countryArray,
    eventStatus,
    isViableRegistrationStatus
} from "../../util/standards-util";
import Button from "@mui/material/Button";
import {useUserRole} from "../../hooks/useUserRole";
import {findFlagUrlByIso2Code} from 'country-flags-svg';
import {
    Alert,
    Chip, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Step,
    StepLabel,
    Stepper, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {StyledTableCell, StyledTableRow} from "./EventTable";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import moment from "moment-timezone";
import {
    useCreateScoringMutation,
    useLazyGetEventPilotRegistrationsQuery,
    useResetScoringMutation
} from "../../slices/apiSlice";
import {sortEntriesPerSgpRules} from "../../pages/Event/PilotRegistrationList";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useNavigate} from "react-router-dom";
import {setEvent} from "../../slices/sessionSlice";

const ScoringSetupWidget = () => {
    const event = useSelector(state => state.session.event)
    const rules = useSelector(state => state.session.eventRules)
    const userRole = useUserRole()

    const navigate = useNavigate()

    const steps = ['Event schedule','Pilots','Settings','Create scoring']
    const [mode, setMode] = useState()
    const [activeStep, setActiveStep] = React.useState();
    const [days, setDays] = React.useState()
    const [entries, setEntries] = React.useState()
    const [eventEntryNo, setEventEntryNo] = React.useState(20)
    const [entriesSelected, setEntriesSelected] = React.useState()
    const [taskSheetAltUnits,setTaskSheetAltUnits] = React.useState("m")
    const [airspaceUnits,setAirspaceUnits] = React.useState("ft")
    const [showActiveAirspace,setShowActiveAirspace] = React.useState(false)
    const [created,setCreated] = React.useState(false)
    const [isResetting, setIsResetting] = React.useState(false)

    const [getPilotRegistrations, getPilotregistrationsResult] =
        useLazyGetEventPilotRegistrationsQuery()

    const [createScoring, createScoringResult] =
        useCreateScoringMutation()

    const [resetScoring, resetScoringResult] =
    useResetScoringMutation()

    const initializeScoring = () => {
        if (!(event?.status<eventStatus.STATUS_SCHEDULED && event.status>eventStatus.STATUS_COMPLETED)) return
        setActiveStep(0)
        setMode('initialize')

        getPilotRegistrations({eventId:event.id})
    }

    const showResetScoringDialog = () => {
        if(!event?.status===eventStatus.STATUS_ACTIVE) return
        setMode('resetDialog')
    }

    const scoringCreated = () => {
        setMode('initializing')
    }

    const handleNextStep = () => {
        if (activeStep===steps.length-1) return
        setActiveStep(activeStep+1)
    }

    const handlePreviousStep = () => {
        if (activeStep===0) return
        setActiveStep(activeStep-1)
    }

    const toggleRestDay =(idx) => {
        days[idx].rest=!days[idx].rest
        let raceCnt=0;
        setDays(days.map((day,i) => {
            if (day.type!=='practice') {
                day.label = day.rest ? "Rest day" : `Day ${++raceCnt}`
            }
            return day;
        }))
    }

    const toggleSelection = (idx) => {
        const newEntries = [...entries]
        newEntries[idx].selected = !newEntries[idx].selected

        let cnt=0
        newEntries.forEach(entry => {
            if (entry.selected) cnt ++
        })

        setEntriesSelected(cnt)
        setEntries(newEntries)
    }

    const toggleShowActiveAirspace = () => {
        setShowActiveAirspace(!showActiveAirspace)
    }

    const handleTaskSheetAltUnitsChange = (event, newValue) => {
        setTaskSheetAltUnits(newValue)
    }

    const handleAirspaceUnitsChange = (event, newValue) => {
        setAirspaceUnits(newValue)
    }

    const doInitialize = () => {
        setActiveStep(steps.length-1)

        if (airspaceUnits==="m") rules.airspaceMapInMeters = 1
            else delete rules.airspaceMapInMeters

        if (taskSheetAltUnits==="ft") rules.altUnits=1
            else delete rules.altUnits

        let p=0;
        let r=0;

        let dayData = [...days]

        for (let i=0;i<dayData.length;i++) {
            var day=dayData[i];
            if (day.rest) {
                //rest
                day.l="Rest day"
                day.t="Rest day - "+day.date.format('dddd, D MMMM yyyy')
            }
            else if (day.type==='practice') {
                //practice
                p++;
                day.l="Practice "+p;
                day.t="Practice "+p+" - "+day.date.format('dddd, D MMMM yyyy')
            }
            else {
                //race
                r++;
                day.l="Race "+r;
                day.t="Race "+r+" - "+day.date.format('dddd, D MMMM yyyy')
            }
            day.date = day.date.format('YYYY-MM-DD')
        }

        const data={
            entries:entries.map(entry=> {
                if (entry.selected) return entry.registration.i
            }).filter( e => e),
            rules:rules,
            days:dayData,
            eventId:event.id,
        }

        createScoring(data)

        setIsResetting(true)
    }

    const doResetScoring = () => {
        const data={
            eventId:event.id,
        }

        resetScoring(data)
    }

    useEffect(() => {
        if (rules) {
            const days = []
            for (let i = 0; i < rules.practiceDays; i++) {
                days.push({
                    label: `Practice ${i + 1}`,
                    type:'practice',
                    date: moment(event.firstDate).add(i, 'days')
                })
            }
            for (let i = 0; i < rules.competitionDays; i++) {
                days.push({
                    label: `Day ${i + 1}`,
                    rest: false,
                    type:'race',
                    date: moment(event.firstRacingDate).add(i, 'days')
                })
            }
            setDays(days)
        }
    }, [rules]);

    useEffect(() => {
        if (getPilotregistrationsResult.isSuccess) {
            const data = getPilotregistrationsResult.data
            let entriesSelected = 0
            const entries = sortEntriesPerSgpRules(
                data.registrations.map((reg, idx)=>{
                    const selected = idx < eventEntryNo && isViableRegistrationStatus(reg.u)

                    if (selected) entriesSelected++

                    return {
                        registration:reg,
                        details:data.details.find( d => d.i === reg.i),
                        pilot:data.pilots.find(p => p.id === reg.p),
                        selected: selected,
                    }
                }),
                event
            )

            setEntriesSelected(entriesSelected)
            setEntries(entries)
        }
    }, [getPilotregistrationsResult]);

    useEffect(() => {
        if (createScoringResult.isSuccess) {
            setIsResetting(false)
            if (createScoringResult.data.status==='ok') {
                setEvent(createScoringResult.data.data)
                navigate('/event/home')
            }
            else {
                //TODO: show error dialog
                console.log('***ERROR while creating scoring')
                console.log(createScoringResult.data.error)
            }
        }
    }, [createScoringResult]);

    useEffect(() => {
        if (resetScoringResult.isSuccess) {
            if (resetScoringResult.data.status==='ok') {
                setEvent(resetScoringResult.data.data)
                navigate('/event/home')
            }
            else {
                //TODO: show error dialog
                console.log('***ERROR while resetting scoring')
                console.log(resetScoringResult.data.error)
            }
        }
    }, [resetScoringResult]);

    return (
        <>
            {(userRole.isAdmin || userRole.isManager) && event?.status<eventStatus.STATUS_SCHEDULED && event.status>eventStatus.STATUS_COMPLETED &&
                <Button variant={'contained'} size={'small'} onClick={initializeScoring}>Initialize scoring</Button>
            }
            {(userRole.isAdmin || userRole.isManager) && event?.status===eventStatus.STATUS_ACTIVE &&
                <Button variant={'contained'} size={'small'} onClick={showResetScoringDialog}>Reset scoring</Button>
            }
            {mode ==='initialize' &&
                <Dialog
                    sx={{ '& .MuiDialog-paper': { p:1, }}}
                    maxWidth="lg"
                    fullWidth={true}
                    open={true}
                >
                    <DialogTitle>Scoring setup</DialogTitle>
                    <DialogContent>
                        <Box mb={4}>
                            <Stepper activeStep={activeStep}>
                                {steps.map( step => { return (
                                    <Step key={step} >
                                        <StepLabel>{step}</StepLabel>
                                    </Step>
                                )})
                                }
                            </Stepper>
                        </Box>
                        {activeStep === 0 &&
                            <>
                            <TableContainer component={'div'}>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Date</StyledTableCell>
                                        <StyledTableCell>Schedule</StyledTableCell>
                                        <StyledTableCell></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {days.map((day,idx) => {
                                        return (
                                            <StyledTableRow
                                                key={day.date}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {border: 0},
                                                    "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                                }}
                                            >
                                                <StyledTableCell>
                                                    {day.date.format('dddd, D MMMM yyyy')}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {day.label}
                                                </StyledTableCell>
                                                <StyledTableCell>{day.type!=='practice' && <Button size='small' variant={'outlined'} sx={{fontSize:'.75em'}} onClick={()=>{toggleRestDay(idx)}}>Set as {day.rest ? "racing" : "rest"} day</Button>}</StyledTableCell>
                                            </StyledTableRow>
                                        )
                                    })
                                    }
                                </TableBody>
                            </Table>
                            </TableContainer>
                            </>
                        }
                        {activeStep === 1 &&
                            <>
                                <TableContainer component={'div'}>
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>Register</StyledTableCell>
                                                <StyledTableCell>#</StyledTableCell>
                                                <StyledTableCell sx={{textAlign:'center'}}>ID</StyledTableCell>
                                                <StyledTableCell>Pilot</StyledTableCell>
                                                <StyledTableCell sx={{textAlign:'center'}}>Nationality</StyledTableCell>
                                                <StyledTableCell sx={{textAlign: 'right'}}>Ranking</StyledTableCell>
                                                <StyledTableCell>Register date</StyledTableCell>
                                                <StyledTableCell>Flarm ID</StyledTableCell>
                                                <StyledTableCell>Status</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {entries.map((entry,idx) => {
                                                const reg= entry.registration
                                                const pilot = entry.pilot
                                                const details = entry.details

                                                const sx = entry.selected ?
                                                    {
                                                        '&:last-child td, &:last-child th': {border: 0},
                                                        "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                                    }
                                                    :
                                                    {
                                                        '&:last-child td, &:last-child th': {border: 0},
                                                        '& td, & th': {borderBottom: 'solid 1px #999999'},
                                                        "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                                        '&:nth-of-type(even)': {
                                                            backgroundColor:'#cccccc',
                                                        },
                                                        '&:nth-of-type(odd)': {
                                                            backgroundColor:'#dddddd',
                                                        },
                                                    }


                                                return (
                                                    <StyledTableRow
                                                        key={reg.i}
                                                        sx={sx}
                                                    >
                                                        <StyledTableCell><Checkbox checked={entry.selected} onChange={()=>{toggleSelection(idx)}}/></StyledTableCell>
                                                        <StyledTableCell component="th" scope="row">
                                                            {idx < eventEntryNo  && isViableRegistrationStatus(reg.u) ? idx + 1 : '-' }
                                                        </StyledTableCell>
                                                        <StyledTableCell component="th" scope="row"  sx={{textAlign:'center'}}>
                                                            { reg.e ?
                                                            <div style={{display:'inline-block',background:'#ffffff',padding:'1px 3px',marginTop:'-2px',marginBottom:'-2px',borderRadius:'4px',verticalAlign:'text-bottom',fontSize:'10pt',border:'1px solid #999999'}}>{reg.e}</div>
                                                                :
                                                                <div>-</div>
                                                            }
                                                        </StyledTableCell>
                                                        <StyledTableCell>{pilot.firstname} {pilot.lastname}</StyledTableCell>
                                                        <StyledTableCell sx={{textAlign:'center'}}><Tooltip title={countryArray.find( c => c.key===pilot.nationality).label} arrow><img src={findFlagUrlByIso2Code(pilot.nationality)} style={{height:'1.2em'}}/></Tooltip></StyledTableCell>
                                                        <StyledTableCell sx={{textAlign: 'right'}}>{reg.m ? reg.m : '-'}</StyledTableCell>
                                                        <StyledTableCell>{moment(reg.r).format("D MMM YYYY")}</StyledTableCell>
                                                        <StyledTableCell>{details.flarmId ? details.flarmId : '-'}</StyledTableCell>
                                                        <StyledTableCell><Chip label={competitionRegistrationStatusLabel[reg.u]} color={competitionRegistrationStatusColor[reg.u]} size={'small'}/></StyledTableCell>
                                                    </StyledTableRow>
                                                )
                                            })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box mt={2} textAlign={'right'}>
                                    <Typography variant={'body1'}>{entriesSelected} entries selected</Typography>
                                </Box>
                            </>
                        }
                        {activeStep === 2 &&
                            <>
                                <Stack spacing={2} maxWidth={'26em'}>
                                    <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',columnGap:2}}>
                                        <Typography variant={'body1'}>
                                            Task sheet altitude units:
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={taskSheetAltUnits}
                                            size = {'small'}
                                            exclusive
                                            onChange={handleTaskSheetAltUnitsChange}
                                        >
                                            <ToggleButton value="m">meters</ToggleButton>
                                            <ToggleButton value="ft">feet</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',columnGap:2}}>
                                        <Typography variant={'body1'}>
                                            MSL airspace in map shown in units:
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={airspaceUnits}
                                            size = {'small'}
                                            exclusive
                                            onChange={handleAirspaceUnitsChange}
                                        >
                                            <ToggleButton value="m">meters</ToggleButton>
                                            <ToggleButton value="ft">feet</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Box>
                                        <FormControlLabel control={<Checkbox checked={showActiveAirspace} onChange={toggleShowActiveAirspace} />} label="Show active airspace in task sheet" />
                                    </Box>
                                </Stack>
                            </>
                        }
                        {activeStep === 3 &&
                            <Box my={10}>
                                {created ?
                                    <Alert severity={'success'}>Scoring has been created for {event.fullEventTitle}</Alert>
                                    :
                                    <Stack sx={{display:'flex',justifyContent:'center',alignItems:'center',flexDirection:'column'}}>
                                        <Typography variant={'body1'} mb={2}>Creating</Typography>
                                        <CircularProgress/>
                                    </Stack>
                                }
                            </Box>
                        }
                    </DialogContent>
                    <DialogActions sx={{justifyContent:'space-between'}}>
                        {activeStep === 0 ?
                            <Button variant={'contained'} onClick={()=>{setMode()}}>Cancel</Button>
                            :
                            activeStep !== steps.length - 1 &&
                             <Button variant={'contained'} onClick={handlePreviousStep}>Previous</Button>
                        }
                        {activeStep<steps.length-2 &&
                            <Button variant={'contained'} onClick={handleNextStep}>Next</Button>
                        }
                        {activeStep === steps.length - 2 &&
                            <Button variant={'contained'} onClick={doInitialize}>Create scoring</Button>
                        }
                        {activeStep === steps.length-1 && created &&
                            <>
                                <Box></Box>
                                <Button variant={'contained'} onClick={scoringCreated}>OK</Button>
                            </>
                        }
                            </DialogActions>
                </Dialog>
            }
            {mode ==='resetDialog' &&
                <>
                    <Dialog
                        sx={{ '& .MuiDialog-paper': { p:1}}}
                        maxWidth="lg"
                        open={true}
                    >
                        <DialogTitle>Reset scoring</DialogTitle>
                        <DialogContent>
                            {isResetting ?
                                <Stack sx={{display:'flex',justifyContent:'center',alignItems:'center',flexDirection:'column'}}>
                                    <Typography variant={'body1'} mb={2}>Resetting</Typography>
                                    <CircularProgress/>
                                </Stack>
                                :
                                <Stack spacing={2}>
                                    <Alert severity={'warning'}>This will delete all scoring information for the SGP and reset scoring to an uninitialized state</Alert>
                                    <Typography variant={'body1'}>Are you sure you would like to proceed?</Typography>
                                </Stack>
                            }
                        </DialogContent>
                        {!isResetting &&
                            <DialogActions>
                                <Button variant={'contained'} onClick={()=>{setMode()}}>Cancel</Button>
                                <Button variant={'contained'} onClick={doResetScoring}>Proceed</Button>
                            </DialogActions>
                        }
                    </Dialog>
                </>
            }
        </>
    );
};

export default ScoringSetupWidget;